import React from 'react';
import Breadcrumbs from '@/components/Breadcrumbs';
import ClassCard, { ClassData } from '@/components/course/ClassCard';
import AssignmentListItem from '@/components/course/SectionCard/AssignmentListItem';
import EvaluationListItem from '@/components/course/SectionCard/EvaluationListItem';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useThemeColors } from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { AssignmentSummary, EvaluationSummary } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { ArrowLeft, BookOpen, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CourseData {
    id: number;
    title: string;
    slug: string;
}

interface SectionData {
    id: number;
    title: string;
}

interface TopicData {
    id: number;
    title: string;
    order: number;
    classes: ClassData[];
    assignments?: AssignmentSummary[];
    evaluations?: EvaluationSummary[];
}

interface Props {
    course: CourseData;
    section: SectionData;
    topic: TopicData;
}

const TopicDetailContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { course, section, topic } = props;
    const colors = useThemeColors();
    
    const breadcrumbItems = [
        { label: t('course.sectionDetail.courses'), href: '/my-account?tab=courses' },
        { label: course.title, href: `/courses/${course.slug}` },
        { label: section.title, href: `/courses/${course.slug}/sections/${section.id}` },
        { label: topic.title }
    ];
    
    const handleClassClick = (classData: ClassData) => {
        // Navigate to class detail page
        window.location.href = `/courses/${course.slug}/classes/${classData.id}`;
    };
    
    return (
        <AppLayout>
            <PageTitle title={topic.title} />
            <div className="bg-gray-50 min-h-screen">
                <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                    {/* Breadcrumbs */}
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    {/* Back to Course Link */}
                    <div className="mb-6">
                        <Link
                            href={`/courses/${course.slug}`}
                            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-200 ${colors.border} ${colors.buttonSecondary}`}
                        >
                            <ArrowLeft size={16} />
                            <span className="font-medium">{t('course.backToCourse')}</span>
                        </Link>
                    </div>
                    
                    {/* Topic Header */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
                        <div className="p-6 lg:p-8">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${colors.accent} ${colors.accentText}`}>
                                            {t('course.topic')} {topic.order}
                                        </div>
                                    </div>
                                    
                                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2 leading-tight">
                                        {topic.title}
                                    </h1>
                                    
                                    <p className="text-gray-600">
                                        {t('course.fromSection')} <span className="font-semibold">{section.title}</span> • <span className="font-semibold">{course.title}</span>
                                    </p>
                                </div>
                                
                                <div className="flex items-center space-x-4">
                                    <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${colors.accent}`}>
                                        <BookOpen className={`w-5 h-5 text-${colors.primary}`} />
                                        <span className={`font-medium ${colors.accentText}`}>
                                            {topic.classes.length} {t('course.classes', { count: topic.classes.length })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Classes Grid */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {t('course.topicClasses')}
                            </h2>
                        </div>
                        
                        {topic.classes.length === 0 ? (
                            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Play className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    {t('course.noClassesAvailable')}
                                </h3>
                                <p className="text-gray-600 max-w-sm mx-auto">
                                    {t('course.noClassesDescription')}
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {topic.classes.map((classData, index) => (
                                    <ClassCard
                                        key={classData.id}
                                        classData={classData}
                                        classNumber={index + 1}
                                        onClick={() => handleClassClick(classData)}
                                    />
                                ))}
                            </div>
                        )}

                        {(topic.assignments || []).length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {t('course.assignments', {
                                        count: topic.assignments?.length || 0,
                                    })}
                                </h3>
                                <div className="rounded-lg border border-gray-200 bg-white p-3">
                                    <div className="space-y-1">
                                        {(topic.assignments || []).map(
                                            (assignment) => (
                                                <AssignmentListItem
                                                    key={`topic-assignment-${assignment.id}`}
                                                    assignment={assignment}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {(topic.evaluations || []).length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-gray-900 capitalize">
                                    {t('course.evaluations', {
                                        count: topic.evaluations?.length || 0,
                                    })}
                                </h3>
                                <div className="rounded-lg border border-gray-200 bg-white p-3">
                                    <div className="space-y-1">
                                        {(topic.evaluations || []).map(
                                            (evaluation) => (
                                                <EvaluationListItem
                                                    key={`topic-evaluation-${evaluation.id}`}
                                                    evaluation={evaluation}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Navigation Footer */}
                    {topic.classes.length > 0 && (
                        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {t('course.readyToStart')}
                                    </h3>
                                    <p className="text-gray-600">
                                        {t('course.startFirstClass')}
                                    </p>
                                </div>
                                
                                <button
                                    onClick={() => handleClassClick(topic.classes[0])}
                                    className="btn-gradient flex items-center space-x-2 rounded-lg px-6 py-3 font-semibold"
                                >
                                    <Play size={18} fill="currentColor" />
                                    <span>{t('course.start')}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
};

const TopicDetail: React.FC<Props> = ({ course, section, topic }) => {
    return (
        <ThemeWrapper>
            <TopicDetailContent />
        </ThemeWrapper>
    );
};

export default TopicDetail;
